import type {
  EmbedVideoBlockUrlData,
  EmbedVideoModel,
} from '@blocksuite/affine-model';
import type { LinkPreviewProvider } from '@blocksuite/affine-shared/services';
import { isAbortError } from '@blocksuite/affine-shared/utils';

import type { EmbedVideoBlockComponent } from './embed-video-block.js';

export async function queryEmbedVideoData(
  embedVideoModel: EmbedVideoModel,
  linkPreviewer: LinkPreviewProvider,
  signal?: AbortSignal
): Promise<Partial<EmbedVideoBlockUrlData>> {
  const url = embedVideoModel.props.url;

  const [videoOpenGraphData, videoOEmbedData] = await Promise.all([
    linkPreviewer.query(url, signal),
    queryVideoOEmbedData(url, signal),
  ]);

  const videoEmbedData: Partial<EmbedVideoBlockUrlData> = {
    ...videoOpenGraphData,
    ...videoOEmbedData,
  };

  if (videoEmbedData.creatorUrl) {
    const creatorOpenGraphData = await linkPreviewer.query(
      videoEmbedData.creatorUrl,
      signal
    );
    videoEmbedData.creatorImage = creatorOpenGraphData.image;
  }

  return videoEmbedData;
}

export async function queryVideoOEmbedData(
  url: string,
  signal?: AbortSignal
): Promise<Partial<EmbedVideoBlockUrlData>> {
  let videoOEmbedData: Partial<EmbedVideoBlockUrlData> = {};

  const oEmbedUrl = `https://youtube.com/oembed?url=${url}&format=json`;

  const oEmbedResponse = await fetch(oEmbedUrl, { signal }).catch(() => null);
  if (oEmbedResponse && oEmbedResponse.ok) {
    const oEmbedJson = await oEmbedResponse.json();
    const { title, author_name, author_url } = oEmbedJson;

    videoOEmbedData = {
      title,
      creator: author_name,
      creatorUrl: author_url,
    };
  }

  return videoOEmbedData;
}

export async function refreshEmbedVideoUrlData(
  embedVideoElement: EmbedVideoBlockComponent,
  signal?: AbortSignal
): Promise<void> {
  let image = null,
    title = null,
    description = null,
    creator = null,
    creatorUrl = null,
    creatorImage = null;

  try {
    embedVideoElement.loading = true;

    // TODO(@mirone): remove service
    const queryUrlData = embedVideoElement.service?.queryUrlData;
    if (!queryUrlData) {
      console.error(
        `Trying to refresh youtube url data, but the queryUrlData is not found.`
      );
      return;
    }

    const youtubeUrlData = await queryUrlData(embedVideoElement.model, signal);

    ({
      image = null,
      title = null,
      description = null,
      creator = null,
      creatorUrl = null,
      creatorImage = null,
    } = youtubeUrlData);

    if (signal?.aborted) return;

    embedVideoElement.store.updateBlock(embedVideoElement.model, {
      image,
      title,
      description,
      creator,
      creatorUrl,
      creatorImage,
    });
  } catch (error) {
    if (signal?.aborted || isAbortError(error)) return;
    throw error;
  } finally {
    embedVideoElement.loading = false;
  }
}
